import axios from 'axios';
import { API_URL } from '../../config/env/env';
import { getUserToken } from '../../utils/helper';

// Points the BGV calls at a local user-service (e.g. http://localhost:9001) while the rest of the ERP
// keeps talking to the configured API.
const BGV_API_URL = process.env.REACT_APP_BGV_API_URL || API_URL;

export type BgvBucket = 'CLEAR' | 'DISCREPANT' | 'ATTENTION_REQUIRED' | 'INSUFFICIENT' | 'IN_PROGRESS';

export interface BgvFilters {
    // Epoch millis: start of the first day and end of the last day.
    startTime: number;
    endTime: number;
    hubId?: number | '';
}

export interface BgvRider {
    userId: number;
    name: string;
    contactNumber: string;
    orgId: number;
    orgName: string;
    cityId: number;
    hubId: number;
    hubName: string;
    bucket: BgvBucket;
    requestId: string;
    caseStatus: string;
    state: string;
    caseSeverity: string;
    initiatedAt: string;
    updatedAt: string;
    reportLink?: string;
}

export interface BgvBatch {
    batchId: number;
    fileName?: string;
    status: string;
    requested: number;
    initiated: number;
    skipped: number;
    failed: number;
    createdAt: string;
    completedAt: string;
    reportUrl?: string;
}

export interface BgvPreview {
    requested: number;
    notFound: number;
    alreadyVerifiedOrInProgress: number;
    toInitiate: number;
}

const headers = () => ({ authtoken: getUserToken() });

// Backend always answers 200 and carries the real outcome in status.code.
const unwrap = (res: any) => {
    const status = res?.data?.status;
    if (status && status.code !== 200) {
        throw new Error(status.message || 'Request failed');
    }
    return res?.data?.response;
};

const clean = (params: Record<string, any>) =>
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null));

export const fetchCounts = async (filters: BgvFilters): Promise<{ counts: Record<BgvBucket, number>; total: number }> => {
    const res = await axios.get(`${BGV_API_URL}/org/staff/bgv/dashboard/counts`, { params: clean({ ...filters }), headers: headers() });
    return unwrap(res);
};

export const fetchRiders = async (
    filters: BgvFilters,
    bucket: BgvBucket | '',
    page: number,
    size: number,
): Promise<{ riders: BgvRider[]; totalElements: number }> => {
    const res = await axios.get(`${BGV_API_URL}/org/staff/bgv/dashboard/riders`, {
        params: clean({ ...filters, bucket, page, size }),
        headers: headers(),
    });
    return unwrap(res);
};

export const backfillByFile = async (file: File, confirm: boolean): Promise<BgvPreview | BgvBatch> => {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post(`${BGV_API_URL}/org/staff/bgv/backfill/file`, form, {
        params: { confirm },
        headers: headers(),
    });
    return unwrap(res);
};

export const fetchBatches = async (page: number, size: number): Promise<{ batches: BgvBatch[]; totalElements: number }> => {
    const res = await axios.get(`${BGV_API_URL}/org/staff/bgv/backfill/batches`, { params: { page, size }, headers: headers() });
    return unwrap(res);
};

export const fetchBatchReport = async (batchId: number): Promise<BgvBatch> => {
    const res = await axios.get(`${BGV_API_URL}/org/staff/bgv/backfill/${batchId}/report`, { headers: headers() });
    return unwrap(res);
};

export type BgvOverrideStatus = 'PASS' | 'IN_PROGRESS' | 'FAIL';

// The backend takes the actor from the token and records the change, old and new status and reason.
export const overrideBgv = async (userId: number, status: BgvOverrideStatus, reason: string): Promise<void> => {
    const res = await axios.post(`${BGV_API_URL}/org/staff/${userId}/bgv/override`, { status, reason }, { headers: headers() });
    unwrap(res);
};

export const retriggerBgv = async (userId: number, address: string, fatherName?: string): Promise<void> => {
    const res = await axios.post(
        `${BGV_API_URL}/org/staff/bgv/manual/trigger`,
        { userId, address, fatherName: fatherName || undefined },
        { headers: headers() },
    );
    unwrap(res);
};

export const fetchHubs = async (orgId: number): Promise<{ id: number; name: string }[]> => {
    const res = await axios.get(`${API_URL}/org/hubs/list/${orgId}/all`, { headers: headers() });
    return unwrap(res)?.hubs ?? [];
};

// What BGV would send for this rider, for ops to confirm or edit before a retrigger.
export interface BgvPrefill {
    firstName: string;
    dob: string;
    fatherName: string;
    address: string;
}

export const fetchBgvPrefill = async (userId: number): Promise<BgvPrefill> => {
    const res = await axios.get(`${BGV_API_URL}/org/staff/${userId}/bgv/prefill`, { headers: headers() });
    const r = unwrap(res);
    return { firstName: r?.firstName ?? '', dob: r?.dob ?? '', fatherName: r?.fatherName ?? '', address: r?.address ?? '' };
};

// Riders at these stores who ordered in the last 7 days, in the upload's columns.
export const fetchRiderSheet = async (hubIds: number[]): Promise<{ fileUrl: string; rowCount: number }> => {
    const res = await axios.get(`${BGV_API_URL}/org/staff/bgv/backfill/rider-sheet`, {
        params: { hubIds: hubIds.join(',') },
        headers: headers(),
    });
    return unwrap(res);
};
